import {
  IHSCodeData,
  ITariffData,
  ITariffExceptionData,
  ITariffRegionData
} from '@/models';
import { IFetchTariffParams, ITariffService } from './tariff.types';
import { AxiosInstance } from 'axios';
import { objectToFormData } from '@/utils/objectToFormData';

export class TariffService implements ITariffService {
  private endpoint: string;

  private ratesEndpoint = 'rates/';

  private exceptionsEndpoint = 'exceptions/';

  private regionsEndpoint = 'regions/';

  constructor(private axios: AxiosInstance) {
    this.endpoint = 'v1/tariffs/';
  }

  fetchTariffRates(params?: IFetchTariffParams): Promise<ITariffData[]> {
    return this.axios
      .get<ITariffData[]>(`${this.endpoint}${this.ratesEndpoint}`, { params })
      .then(({ data }) => data);
  }

  fetchTariffRate(id: number): Promise<ITariffData> {
    return this.axios
      .get<ITariffData>(`${this.endpoint}${this.ratesEndpoint}${id}/`)
      .then(({ data }) => data);
  }

  createTariffRate(data: ITariffData): Promise<ITariffData> {
    return this.axios
      .post<ITariffData>(`${this.endpoint}${this.ratesEndpoint}`, data)
      .then(({ data }) => data);
  }

  updateTariffRate(data: ITariffData): Promise<ITariffData> {
    return this.axios
      .patch<ITariffData>(
        `${this.endpoint}${this.ratesEndpoint}${data.id}/`,
        data
      )
      .then(({ data }) => data);
  }

  deleteTariffRate(id: number): Promise<void> {
    return this.axios
      .delete(`${this.endpoint}${this.ratesEndpoint}${id}/`)
      .then(({ data }) => data);
  }

  fetchTariffRegions(): Promise<ITariffRegionData[]> {
    return this.axios
      .get<ITariffRegionData[]>(`${this.endpoint}${this.regionsEndpoint}`)
      .then(({ data }) => data);
  }

  fetchTariffRegion(id: number): Promise<ITariffRegionData> {
    return this.axios
      .get<ITariffRegionData>(`${this.endpoint}${this.regionsEndpoint}${id}/`)
      .then(({ data }) => data);
  }

  createTariffRegion(data: ITariffRegionData): Promise<ITariffRegionData> {
    return this.axios
      .post<ITariffRegionData>(`${this.endpoint}${this.regionsEndpoint}`, data)
      .then(({ data }) => data);
  }

  updateTariffRegion(data: ITariffRegionData): Promise<ITariffRegionData> {
    return this.axios
      .patch<ITariffRegionData>(
        `${this.endpoint}${this.regionsEndpoint}${data.id}/`,
        data
      )
      .then(({ data }) => data);
  }

  deleteTariffRegion(id: number): Promise<void> {
    return this.axios
      .delete(`${this.endpoint}${this.regionsEndpoint}${id}/`)
      .then(({ data }) => data);
  }

  fetchTariffExceptions(): Promise<ITariffExceptionData[]> {
    return this.axios
      .get<ITariffExceptionData[]>(`${this.endpoint}${this.exceptionsEndpoint}`)
      .then(({ data }) => data);
  }

  fetchTariffException(id: number): Promise<ITariffExceptionData> {
    return this.axios
      .get<ITariffExceptionData>(
        `${this.endpoint}${this.exceptionsEndpoint}${id}/`
      )
      .then(({ data }) => data);
  }

  createTariffException(
    data: ITariffExceptionData
  ): Promise<ITariffExceptionData> {
    return this.axios
      .post<ITariffExceptionData>(
        `${this.endpoint}${this.exceptionsEndpoint}`,
        data
      )
      .then(({ data }) => data);
  }

  updateTariffException(
    data: ITariffExceptionData
  ): Promise<ITariffExceptionData> {
    return this.axios
      .patch<ITariffExceptionData>(
        `${this.endpoint}${this.exceptionsEndpoint}${data.id}/`,
        data
      )
      .then(({ data }) => data);
  }

  deleteTariffException(id: number): Promise<void> {
    return this.axios
      .delete(`${this.endpoint}${this.exceptionsEndpoint}${id}/`)
      .then(({ data }) => data);
  }

  uploadTariffExceptions(file: File): Promise<ITariffExceptionData[]> {
    const formData = objectToFormData({ file: file });
    return this.axios
      .post(
        `${this.endpoint}${this.exceptionsEndpoint}upload-exceptions/`,
        formData
      )
      .then(({ data }) => data);
  }
}
